class Logger {
    static get instance() {
        if (!this._instance) {
            this._instance = new Logger();
        }
        return this._instance;
    }
    constructor() {
        var _a, _b, _c, _d;
        this._minLevel = 'info';
        this._enabled = process.env.NODE_ENV === 'development';
        this._showTimestamp = true;
        this._useColors = true;
        this._defaultModule = 'CommonGameUI';
        this._levelColors = {
            debug: '#7B68EE', // Medium slate blue
            info: '#4299E1', // Blue
            warn: '#F6AD55', // Orange
            error: '#F56565', // Red
        };
        this._moduleColors = [
            '#805AD5', // Purple
            '#3182CE', // Blue
            '#38A169', // Green
            '#DD6B20', // Orange
            '#E53E3E', // Red
            '#319795', // Teal
            '#D69E2E', // Yellow
        ];
        this._levelPriority = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3,
        };
        // Check if we're in a browser environment
        if (typeof window !== 'undefined') {
            // Apply saved settings from localStorage if they exist
            try {
                const savedSettings = localStorage.getItem('commonGameUI_loggerSettings');
                if (savedSettings) {
                    const settings = JSON.parse(savedSettings);
                    this._enabled = (_a = settings.enabled) !== null && _a !== void 0 ? _a : this._enabled;
                    this._minLevel = (_b = settings.minLevel) !== null && _b !== void 0 ? _b : this._minLevel;
                    this._showTimestamp = (_c = settings.showTimestamp) !== null && _c !== void 0 ? _c : this._showTimestamp;
                    this._useColors = (_d = settings.useColors) !== null && _d !== void 0 ? _d : this._useColors;
                }
            }
            catch (e) {
                // Silently ignore localStorage errors
            }
        }
    }
    /**
     * Configure the logger
     */
    configure({ enabled, minLevel, timestamp, colors, defaultModule, }) {
        if (enabled !== undefined)
            this._enabled = enabled;
        if (minLevel)
            this._minLevel = minLevel;
        if (timestamp !== undefined)
            this._showTimestamp = timestamp;
        if (colors !== undefined)
            this._useColors = colors;
        if (defaultModule)
            this._defaultModule = defaultModule;
        // Save settings to localStorage
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem('commonGameUI_loggerSettings', JSON.stringify({
                    enabled: this._enabled,
                    minLevel: this._minLevel,
                    showTimestamp: this._showTimestamp,
                    useColors: this._useColors,
                }));
            }
            catch (e) {
                // Silently ignore localStorage errors
            }
        }
        return this;
    }
    /**
     * Enable or disable all logging
     */
    enable(enabled = true) {
        return this.configure({ enabled });
    }
    /**
     * Get a module-specific hash code for consistent coloring
     */
    getModuleColorIndex(module) {
        let hash = 0;
        for (let i = 0; i < module.length; i++) {
            hash = (hash << 5) - hash + module.charCodeAt(i);
            hash |= 0; // Convert to 32bit integer
        }
        return Math.abs(hash) % this._moduleColors.length;
    }
    /**
     * Format a log message with timestamps and colors
     */
    formatLog(level, module, args) {
        if (!this._enabled || this._levelPriority[level] < this._levelPriority[this._minLevel]) {
            return []; // Return empty array to skip logging
        }
        if (this._useColors) {
            const moduleColorIndex = this.getModuleColorIndex(module);
            const timestamp = this._showTimestamp
                ? `%c${new Date().toISOString().split('T')[1].slice(0, -1)}`
                : '';
            const levelStr = `%c${level.toUpperCase()}`;
            const moduleStr = `%c[${module}]`;
            const styles = [
                this._showTimestamp ? 'color: gray; font-weight: normal;' : '',
                `color: ${this._levelColors[level]}; font-weight: bold;`,
                `color: ${this._moduleColors[moduleColorIndex]}; font-weight: bold;`,
            ].filter(Boolean);
            return [`${timestamp} ${levelStr} ${moduleStr}`, ...styles, ...args];
        }
        else {
            const timestamp = this._showTimestamp
                ? `${new Date().toISOString().split('T')[1].slice(0, -1)}`
                : '';
            const prefix = `${timestamp} ${level.toUpperCase()} [${module}]`.trim();
            return [prefix, ...args];
        }
    }
    /**
     * Internal log method
     */
    _log(level, options, ...args) {
        const opts = {
            level,
            module: (options === null || options === void 0 ? void 0 : options.module) || this._defaultModule,
            timestamp: (options === null || options === void 0 ? void 0 : options.timestamp) !== undefined ? options.timestamp : this._showTimestamp,
            color: (options === null || options === void 0 ? void 0 : options.color) !== undefined ? options.color : this._useColors,
        };
        // Override instance settings with call-specific options
        const prevTimestamp = this._showTimestamp;
        const prevColors = this._useColors;
        this._showTimestamp = opts.timestamp;
        this._useColors = opts.color;
        const logArgs = this.formatLog(level, opts.module, args);
        // Reset to previous settings
        this._showTimestamp = prevTimestamp;
        this._useColors = prevColors;
        if (logArgs.length === 0)
            return;
        switch (level) {
            case 'debug':
                console.debug(...logArgs);
                break;
            case 'info':
                console.info(...logArgs);
                break;
            case 'warn':
                console.warn(...logArgs);
                break;
            case 'error':
                console.error(...logArgs);
                break;
        }
    }
    /**
     * Log debug message
     */
    debug(optionsOrArg, ...args) {
        if (typeof optionsOrArg === 'object' && optionsOrArg !== null && 'module' in optionsOrArg) {
            this._log('debug', optionsOrArg, ...args);
        }
        else {
            this._log('debug', undefined, optionsOrArg, ...args);
        }
    }
    /**
     * Log info message
     */
    info(optionsOrArg, ...args) {
        if (typeof optionsOrArg === 'object' && optionsOrArg !== null && 'module' in optionsOrArg) {
            this._log('info', optionsOrArg, ...args);
        }
        else {
            this._log('info', undefined, optionsOrArg, ...args);
        }
    }
    /**
     * Log warning message
     */
    warn(optionsOrArg, ...args) {
        if (typeof optionsOrArg === 'object' && optionsOrArg !== null && 'module' in optionsOrArg) {
            this._log('warn', optionsOrArg, ...args);
        }
        else {
            this._log('warn', undefined, optionsOrArg, ...args);
        }
    }
    /**
     * Log error message
     */
    error(optionsOrArg, ...args) {
        if (typeof optionsOrArg === 'object' && optionsOrArg !== null && 'module' in optionsOrArg) {
            this._log('error', optionsOrArg, ...args);
        }
        else {
            this._log('error', undefined, optionsOrArg, ...args);
        }
    }
    /**
     * Log table data
     */
    table(data, columns) {
        if (!this._enabled || this._levelPriority['info'] < this._levelPriority[this._minLevel]) {
            return;
        }
        const timestamp = this._showTimestamp
            ? `${new Date().toISOString().split('T')[1].slice(0, -1)} `
            : '';
        console.log(`${timestamp}TABLE [${this._defaultModule}]`);
        console.table(data, columns);
    }
    /**
     * Create a group of logs
     */
    group(label, collapsed = false) {
        if (!this._enabled)
            return;
        const timestamp = this._showTimestamp
            ? `${new Date().toISOString().split('T')[1].slice(0, -1)} `
            : '';
        const groupLabel = `${timestamp}GROUP [${this._defaultModule}] ${label}`;
        if (collapsed) {
            console.groupCollapsed(groupLabel);
        }
        else {
            console.group(groupLabel);
        }
    }
    groupEnd() {
        if (!this._enabled)
            return;
        console.groupEnd();
    }
    /**
     * Time an operation
     */
    time(label) {
        if (!this._enabled)
            return;
        console.time(`[${this._defaultModule}] ${label}`);
    }
    timeEnd(label) {
        if (!this._enabled)
            return;
        console.timeEnd(`[${this._defaultModule}] ${label}`);
    }
    /**
     * Create a module-specific logger
     */
    createModuleLogger(module) {
        return new ModuleLogger(this, module);
    }
}
/**
 * Module-specific logger that includes the module name in all logs
 */
class ModuleLogger {
    constructor(logger, module) {
        this.logger = logger;
        this.module = module;
    }
    debug(...args) {
        this.logger.debug({ module: this.module }, ...args);
    }
    info(...args) {
        this.logger.info({ module: this.module }, ...args);
    }
    warn(...args) {
        this.logger.warn({ module: this.module }, ...args);
    }
    error(...args) {
        this.logger.error({ module: this.module }, ...args);
    }
    table(data, columns) {
        this.logger.table(data, columns);
    }
    group(label, collapsed = false) {
        this.logger.group(`[${this.module}] ${label}`, collapsed);
    }
    groupEnd() {
        this.logger.groupEnd();
    }
    time(label) {
        this.logger.time(`[${this.module}] ${label}`);
    }
    timeEnd(label) {
        this.logger.timeEnd(`[${this.module}] ${label}`);
    }
}
// Export a singleton instance
export const logger = Logger.instance;
